import React, { useCallback, useState, createContext, useContext, Component } from 'react';
export type Locale = 'en' | 'pt';
const translations: Record<Locale, Record<string, string>> = {
  en: {
    // Navigation
    'nav.route': 'Route',
    'nav.pacing': 'Pacing',
    'nav.nutrition': 'Nutrition',
    'nav.settings': 'Settings',
    // Header
    'header.export': 'Export race plan',
    'header.import': 'Import race plan',
    'header.newPlan': 'Start a new plan?',
    'header.help': 'Help & Support',
    // Import Screen
    'import.title1': 'Plan your race.',
    'import.title2': 'Master the trail.',
    'import.subtitle': 'Import your GPX file and get detailed pacing strategies, checkpoint planning, and race-day insights.',
    'import.dropTitle': 'Drop your GPX file here',
    'import.dropSubtitle': 'or click to browse from your device',
    'import.selectGpx': 'Select GPX File',
    'import.or': 'or',
    'import.racePlan': 'Import a Race Plan',
    'import.racePlanDesc': 'Load a previously exported .json file with route, pacing, and nutrition data',
    'import.demo': 'Or try with a demo route →',
    'import.features.checkpoints': 'Checkpoints',
    'import.features.pacing': 'Pacing',
    'import.features.strategy': 'Strategy',
    'import.footer': 'Supports GPX files from Strava, Garmin, Coros, and other platforms',
    // Course Tab
    'course.raceName': 'Race Name',
    'course.raceNamePlaceholder': 'Enter race name',
    'course.startDateTime': 'Start Date & Time',
    'course.title': 'Route',
    'course.subtitle': 'Define your course, set race details and manage checkpoints',
    'course.distance': 'Distance',
    'course.elevationPlus': 'Elevation +',
    'course.elevationMinus': 'Elevation -',
    'course.sunrise': 'Sunrise',
    'course.sunset': 'Sunset',
    'course.elevation': 'Elevation',
    'course.map': 'Map',
    'course.route': 'Route',
    'course.elevationProfile': 'Elevation Profile',
    'course.clickToAdd': 'Click to add checkpoint',
    'course.tapToAdd': 'Tap chart to add checkpoint',
    'course.checkpoints': 'Checkpoints',
    'course.checkpointsDesc': 'Aid stations, cutoffs & segments',
    'course.add': 'Add',
    // Segments Table
    'segments.name': 'Name',
    'segments.type': 'Type',
    'segments.terrain': 'Terrain',
    'segments.dist': 'Dist',
    'segments.dPlus': 'D+',
    'segments.cutoff': 'Cutoff',
    'segments.noCheckpoints': 'No checkpoints yet',
    'segments.noCheckpointsDesc': 'Add a split to create segments',
    'segments.checkpointName': 'Checkpoint name',
    // Column tooltips — Segments
    'tooltip.segments.name': 'Name or identifier of the checkpoint or aid station.',
    'tooltip.segments.type': 'Category of checkpoint: Base Camp, Aid Station, Water Point, or Other.',
    'tooltip.segments.terrain': 'Surface type of this segment — affects pacing calculations.',
    'tooltip.segments.dist': 'Distance covered within this segment (km).',
    'tooltip.segments.dPlus': 'Total elevation gain in this segment (meters).',
    'tooltip.segments.cutoff': 'Maximum allowed time from race start to reach this checkpoint.',
    // Column tooltips — Pacing
    'tooltip.pacing.checkpoint': 'Name of the checkpoint or aid station at the end of this segment.',
    'tooltip.pacing.type': 'Category of checkpoint: Base Camp, Aid Station, Water Point, or Other.',
    'tooltip.pacing.terrain': 'Surface type of this segment — affects pacing calculations.',
    'tooltip.pacing.dist': 'Distance covered within this segment (km).',
    'tooltip.pacing.dPlus': 'Total elevation gain in this segment (meters).',
    'tooltip.pacing.effortPace': 'Adjusted for terrain & elevation. Reflects effort, not flat-equivalent pace — not directly comparable across segments.',
    'tooltip.pacing.stop': 'Planned stop time at this checkpoint (minutes).',
    'tooltip.pacing.segment': 'Total time for this segment including any stop time.',
    'tooltip.pacing.eta': 'Estimated time of arrival at this checkpoint.',
    'tooltip.pacing.buffer': 'Time margin before the cutoff deadline. Green = safe, yellow = tight, red = over.',
    // Column tooltips — Nutrition
    'tooltip.nutrition.cho': 'Carbohydrates: planned intake vs. target for this segment (grams).',
    'tooltip.nutrition.cal': 'Calories: planned intake vs. target for this segment (kcal).',
    // Column tooltips — Product Library
    'tooltip.products.product': 'Name of the nutrition product.',
    'tooltip.products.unit': 'Serving unit — e.g. gel, bar, scoop, bottle.',
    'tooltip.products.cho': 'Carbohydrates per serving unit (grams).',
    'tooltip.products.cal': 'Calories per serving unit (kcal).',
    'tooltip.products.sodium': 'Sodium per serving unit (milligrams).',
    // Checkpoint Types
    'checkpoint.None': 'None',
    'checkpoint.BV': 'Base Camp',
    'checkpoint.Aid': 'Aid Station',
    'checkpoint.Water': 'Water Only',
    'checkpoint.Other': 'Other',
    'checkpoint.short.None': '—',
    'checkpoint.short.BV': 'BV',
    'checkpoint.short.Aid': 'Aid',
    'checkpoint.short.Water': 'Water',
    'checkpoint.short.Other': 'Other',
    // Terrain Types
    'terrain.Road': 'Road',
    'terrain.SmoothTrail': 'Easy Trail',
    'terrain.MixedTrail': 'Mixed Trail',
    'terrain.Rocky': 'Rocky',
    'terrain.Technical': 'Technical',
    'terrain.MudSand': 'Mud / Sand',
    'terrain.OfftrailSteep': 'Off-trail / Steep',
    // Pacing
    'pacing.title': 'Pacing Strategy',
    'pacing.subtitle': 'Configure stop times and adjust pace per segment',
    'pacing.model': 'Model',
    'pacing.pacingModel': 'Pacing Model:',
    'pacing.customOverridden': 'Custom (Overridden)',
    'pacing.custom': 'Custom',
    'pacing.executionStrategy': 'Execution Strategy:',
    'pacing.totalTime': 'Total Time',
    'pacing.finish': 'Finish',
    'pacing.checkpoint': 'Checkpoint',
    'pacing.pace': 'Pace',
    'pacing.paceUnit': 'min/km',
    'pacing.stop': 'Stop',
    'pacing.stopTime': 'Stop Time',
    'pacing.segment': 'Segment',
    'pacing.elapsed': 'Elapsed',
    'pacing.accumulated': 'Accumulated',
    'pacing.eta': 'ETA',
    'pacing.effortPace': 'Effort Pace',
    'pacing.buffer': 'Buffer',
    'pacing.scenario': 'Execution Scenario',
    'pacing.segments': '{count} segments',
    'pacing.night': 'Night',
    'pacing.day': 'Day',
    'pacing.hot': 'Hot',
    'pacing.baseCamp': 'BASE CAMP',
    'pacing.long': 'Long',
    'pacing.steep': 'Steep',
    'scenario.optimal': 'Optimal',
    'scenario.standard': 'Standard',
    'scenario.safe': 'Safe',
    // Nutrition
    'nutrition.title': 'Nutrition Plan',
    'nutrition.subtitle': 'Plan your fueling strategy per segment',
    'nutrition.choBalance': 'CHO Balance',
    'nutrition.calBalance': 'CAL Balance',
    'nutrition.productsUsed': 'Products Used',
    'nutrition.totalUsage': 'Total Product Usage',
    'nutrition.needed': 'needed',
    'nutrition.ofLibrary': 'of {count} in library',
    'nutrition.addProduct': 'Add product…',
    'nutrition.noProducts': 'No products added yet',
    'nutrition.noPacing': 'No pacing data available',
    'nutrition.noPacingDesc': 'Configure your route and pacing first',
    'nutrition.overallStatus': 'Overall Status',
    'nutrition.segmentBreakdown': 'Segment Breakdown',
    'nutrition.packingList': 'Packing List',
    'nutrition.itemsToCarry': '{count} items to carry',
    'nutrition.plannedProducts': 'Planned Products',
    'nutrition.noProductsYet': 'No products added yet',
    'nutrition.tapToPlan': 'Tap to plan',
    'nutrition.fluidGuidance': 'Aim for ~{amount}ml of fluid. Guidance only.',
    'nutrition.recommendedFluid': 'Recommended fluid intake:',
    'nutrition.searchProduct': 'Search to add product...',
    'nutrition.productLibraryDesc': 'Manage your nutrition products for race planning.',
    'nutrition.stateIncomplete': 'Incomplete',
    'nutrition.stateNeedsAdjustment': 'Needs adjustment',
    'nutrition.stateOnTarget': 'On target',
    'nutrition.aboveTargetNote': 'Planned intake is above the recommended target. This is acceptable for shorter segments or personal preference — guidance only.',
    'nutrition.targets': 'Nutrition Targets',
    'nutrition.targetsSubtitle': 'Hourly intake driving your nutrition plan',
    'nutrition.targetsInfo': 'These values are derived from your athlete profile (effort level & sweat rate). You can fine-tune them here — changes apply directly to your nutrition plan targets.',
    'nutrition.carbs': 'Carbs',
    'nutrition.calories': 'Calories',
    'nutrition.fluids': 'Fluids',
    'nutrition.sodium': 'Sodium',
    'nutrition.carbsDesc': 'Carbohydrate intake target per hour. Higher intensity efforts require more carbs to sustain energy. Most athletes tolerate 60–90g/h; trained gut can handle up to 120g/h.',
    'nutrition.caloriesDesc': 'Caloric intake target per hour. Derived from carb intake (~4 kcal per gram of carbs). Adjust if you rely on fat-based fuels or mixed sources.',
    'nutrition.fluidsDesc': 'Fluid intake target per hour. Heavier sweaters and hot conditions require more. Aim to replace 60–80% of sweat losses. Over-drinking can be as dangerous as under-drinking.',
    'nutrition.sodiumDesc': 'Sodium intake target per hour. Crucial for preventing hyponatremia and cramps. Heavy sweaters and hot conditions demand more. Typical range is 300–800 mg/h depending on sweat rate.',
    // Settings
    'settings.title': 'Settings',
    'settings.subtitle': 'Manage your products, nutrition targets, and pacing models',
    'settings.productLibrary': 'Product Library',
    'settings.nutrition': 'Nutrition',
    'settings.pacingModels': 'Pacing Models',
    'settings.pacingModel': 'Pacing Model',
    'settings.pacingModelNote': 'All models are automatically calibrated using your global Athlete Profile. No manual tuning required.',
    'settings.raceDefaults': 'Race Defaults',
    'settings.scenarios': 'Execution Scenarios',
    'settings.scenariosDesc': 'Adjust how conservative the pacing plan is. Standard adds friction for terrain and fatigue; Safe adds extra buffers for risk management.',
    'settings.safeIntensity': 'Safe Mode Intensity',
    'settings.safeIntensityDesc': 'How aggressively should the "Safe" scenario apply penalties?',
    'settings.safeIntensityNote': 'Adjust the severity of late-race fatigue penalties in Safe mode.',
    'intensity.low': 'Low',
    'intensity.default': 'Default',
    'intensity.high': 'High',
    'settings.terrainFactors': 'Terrain Factors',
    'settings.terrainFactorsDesc': 'Adjust pace multiplier, uphill penalty and downhill adjustment per terrain type',
    'settings.terrainFactor': 'Pace Factor',
    'settings.uphillSec': 'Uphill (s/100m)',
    'settings.downhillSec': 'Downhill (s/100m)',
    'settings.resetDefaults': 'Reset to Defaults',
    'settings.search': 'Search',
    'settings.searchProducts': 'Search products…',
    'settings.export': 'Export',
    'settings.exportProducts': 'Export Products',
    'settings.import': 'Import',
    'settings.importProducts': 'Import Products',
    'settings.addProduct': 'Add Product',
    'settings.productName': 'Name',
    'settings.unit': 'Unit',
    'settings.carbs': 'Carbs (g)',
    'settings.cals': 'Cals',
    'settings.sodium': 'Sodium (mg)',
    'settings.save': 'Save',
    'settings.cancel': 'Cancel',
    'settings.product': 'Product',
    'settings.noProducts': 'No products yet',
    'settings.noMatch': 'No products match your search',
    'settings.removeProduct': 'Remove "{name}" from your library?',
    'settings.hourlyTargets': 'Hourly Targets',
    'settings.hourlyTargetsDesc': 'Default targets applied to all segments unless overridden',
    'settings.choTarget': 'CHO Target (g/h)',
    'settings.calTarget': 'CAL Target (kcal/h)',
    'settings.alertThresholds': 'Alert Thresholds',
    'settings.alertThresholdsDesc': 'Highlight deficits in red when they exceed these values',
    'settings.choDeficitAlert': 'CHO Deficit Alert (g)',
    'settings.calDeficitAlert': 'CAL Deficit Alert (kcal)',
    'settings.active': 'Active',
    'settings.activate': 'Activate',
    'settings.language': 'Language',
    'settings.athleteProfile': 'Athlete Profile',
    'settings.executionStrategy': 'Execution Strategy',
    'settings.checkpointStopDefaults': 'Checkpoint Stop Defaults (min)',
    // Product table headers
    'products.product': 'Product',
    'products.unit': 'Unit',
    'products.cho': 'CHO',
    'products.cal': 'CAL',
    'products.sodium': 'Na+',
    'products.removeTitle': 'Remove Product',
    'products.removeConfirm': 'Delete',
    'products.delete': 'Delete {name}?',
    // Checkpoint stop defaults
    'settings.checkpointDefaults': 'Default Stop Times',
    'settings.checkpointDefaultsDesc': 'Stop time automatically applied when selecting a checkpoint type',
    'settings.defaultStop.Water': 'Water Only (min)',
    'settings.defaultStop.Aid': 'Aid Station (min)',
    'settings.defaultStop.BV': 'Base Camp (min)',
    'settings.defaultStop.Other': 'Other (min)',
    // Add Split Dialog
    'split.title': 'Add Checkpoint',
    'split.totalCourse': 'Total course:',
    'split.distance': 'Distance (km)',
    'split.distancePlaceholder': 'e.g. 12.5',
    'split.name': 'Name (optional)',
    'split.namePlaceholder': 'e.g. Aid Station 1',
    'split.distanceError': 'Enter a distance between 0 and {max} km',
    'split.add': 'Add Checkpoint',
    // Common
    'common.cancel': 'Cancel',
    'common.save': 'Save',
    'common.back': 'Back',
    'common.next': 'Next',
    'common.min': 'min',
    'common.total': 'Total',
    'common.fromLibrary': 'From your library',
    'common.confirm': 'Confirm',
    // Confirm dialogs
    'confirm.newPlan.title': 'Start a new plan?',
    'confirm.newPlan.message': 'This will clear all your current data including route, pacing, and nutrition plans. This action cannot be undone.',
    'confirm.newPlan.confirm': 'Yes, start new',
    'confirm.removeProduct.title': 'Remove product?',
    'confirm.removeProduct.message': 'Remove "{name}" from your library? This cannot be undone.',
    'confirm.removeProduct.confirm': 'Remove',
    // Athlete Profile
    'athlete.title': 'Athlete Profile',
    'athlete.subtitle': 'Your personalized calibration driving all calculations.',
    'athlete.recalibrate': 'Recalibrate',
    'athlete.flatEffort': 'Flat Effort',
    'athlete.uphillStrategy': 'Uphill Strategy',
    'athlete.heatTolerance': 'Heat Tolerance',
    'athlete.sweatRate': 'Sweat Rate',
    'athlete.fast': 'Fast',
    'athlete.insightsTitle': 'What this means for your race:',
    'athlete.insightHeat': 'Heat Sensitivity: Plan includes aggressive hydration & sodium increases in warm weather.',
    'athlete.insightSweat': 'Heavy Sweater: Baseline hydration targets are elevated (+30-40%).',
    'athlete.insightFatigue': 'Early Fatigue: Pacing model applies conservative decay after 3 hours.',
    'athlete.insightRisk': 'Conservative Strategy: Plan biases towards safety margins and slower starts.',
    // Calibration Wizard
    'calibration.title': 'Athlete Calibration',
    'calibration.welcome': 'Welcome',
    'calibration.review': 'Review',
    'calibration.questionOf': 'Question {current} of {total}',
    'calibration.introTitle': "Let's calibrate your profile",
    'calibration.introDesc': 'To generate an accurate race plan, we need to understand your running style, fatigue resistance, and climate sensitivity. This takes about 1 minute.',
    'calibration.benefit1': 'Determines your sustainable pace & fueling needs',
    'calibration.benefit2': 'Sets uphill/downhill strategy automatically',
    'calibration.benefit3': 'Adjusts for heat, cold, and late-race fatigue',
    'calibration.start': 'Start Calibration',
    'calibration.reviewTitle': 'Profile Calibrated!',
    'calibration.reviewDesc': "Your athlete profile is ready. We've derived your baseline pacing, nutrition targets, and climate adjustments.",
    'calibration.saveProfile': 'Save Profile & Generate Plan',
    'calibration.flatPace': 'Flat Pace',
    'calibration.uphill': 'Uphill',
    'calibration.downhill': 'Downhill',
    'calibration.fatigueOnset': 'Fatigue Onset',
    'calibration.riskProfile': 'Risk Profile',
    'calibration.coldStart': 'Cold Start',
    // Calibration Questions
    'calibration.q.flatEffort.title': 'Sustainable Flat Effort',
    'calibration.q.flatEffort.desc': 'On a flat, runnable trail after several hours, what is your sustainable pace?',
    'calibration.q.flatEffort.sub530': 'Fast (< 5:30 min/km)',
    'calibration.q.flatEffort.sub530Desc': 'High intensity, efficient runner.',
    'calibration.q.flatEffort.steady': 'Steady (5:30 - 6:00 min/km)',
    'calibration.q.flatEffort.steadyDesc': 'Strong, controlled aerobic effort.',
    'calibration.q.flatEffort.comfortable': 'Comfortable (6:00 - 6:30 min/km)',
    'calibration.q.flatEffort.comfortableDesc': 'Conversational, endurance pace.',
    'calibration.q.flatEffort.easy': 'Easy (> 6:30 min/km)',
    'calibration.q.flatEffort.easyDesc': 'Conservative pacing, focus on finishing.',
    'calibration.q.uphillStrategy.title': 'Uphill Strategy',
    'calibration.q.uphillStrategy.desc': 'How do you handle long climbs?',
    'calibration.q.uphillStrategy.hikeEarly': 'Hike Early',
    'calibration.q.uphillStrategy.hikeEarlyDesc': 'Switch to hiking quickly to save legs.',
    'calibration.q.uphillStrategy.mixed': 'Mixed',
    'calibration.q.uphillStrategy.mixedDesc': 'Run shallow grades, hike steep ones.',
    'calibration.q.uphillStrategy.runMost': 'Run Most',
    'calibration.q.uphillStrategy.runMostDesc': 'Aggressive climbing, running as much as possible.',
    'calibration.q.downhillHandling.title': 'Downhill Handling',
    'calibration.q.downhillHandling.desc': 'How do you handle technical descents?',
    'calibration.q.downhillHandling.cautious': 'Cautious',
    'calibration.q.downhillHandling.cautiousDesc': 'I slow down significantly to be safe.',
    'calibration.q.downhillHandling.controlled': 'Controlled',
    'calibration.q.downhillHandling.controlledDesc': 'Steady pace, not taking big risks.',
    'calibration.q.downhillHandling.aggressive': 'Aggressive',
    'calibration.q.downhillHandling.aggressiveDesc': 'I attack descents and make up time.',
    'calibration.q.fatigue.title': 'Fatigue Onset',
    'calibration.q.fatigue.desc': 'When do you typically start to feel significant fatigue?',
    'calibration.q.fatigue.early': '< 3 Hours',
    'calibration.q.fatigue.earlyDesc': 'Short endurance.',
    'calibration.q.fatigue.mid': '3 - 5 Hours',
    'calibration.q.fatigue.midDesc': 'Medium endurance.',
    'calibration.q.fatigue.late': '5 - 7 Hours',
    'calibration.q.fatigue.lateDesc': 'Long endurance.',
    'calibration.q.fatigue.ultra': '8+ Hours',
    'calibration.q.fatigue.ultraDesc': 'Ultra endurance.',
    'calibration.q.riskTolerance.title': 'Risk Tolerance',
    'calibration.q.riskTolerance.desc': 'What is your primary goal for this race?',
    'calibration.q.riskTolerance.finish': 'Finish First',
    'calibration.q.riskTolerance.finishDesc': 'Prioritize finishing safely. Avoid blowing up.',
    'calibration.q.riskTolerance.balanced': 'Balanced',
    'calibration.q.riskTolerance.balancedDesc': 'Balance performance with safety.',
    'calibration.q.riskTolerance.performance': 'Performance',
    'calibration.q.riskTolerance.performanceDesc': 'Push limits for a PR. Accept higher risk.',
    'calibration.q.heatTolerance.title': 'Heat Tolerance',
    'calibration.q.heatTolerance.desc': 'How does heat affect your performance?',
    'calibration.q.heatTolerance.sensitive': 'Sensitive',
    'calibration.q.heatTolerance.sensitiveDesc': 'Heat slows me down significantly early on.',
    'calibration.q.heatTolerance.moderate': 'Moderate',
    'calibration.q.heatTolerance.moderateDesc': 'I notice it, but manage okay.',
    'calibration.q.heatTolerance.tolerant': 'Tolerant',
    'calibration.q.heatTolerance.tolerantDesc': 'I perform well in hot conditions.',
    'calibration.q.sweatRate.title': 'Perceived Sweat Rate',
    'calibration.q.sweatRate.desc': 'How much do you sweat compared to others?',
    'calibration.q.sweatRate.heavy': 'Heavy',
    'calibration.q.sweatRate.heavyDesc': 'Soak through clothes quickly. Salt stains.',
    'calibration.q.sweatRate.normal': 'Normal',
    'calibration.q.sweatRate.normalDesc': 'Average sweating.',
    'calibration.q.sweatRate.low': 'Low',
    'calibration.q.sweatRate.lowDesc': 'Stay relatively dry.',
    'calibration.q.coldStart.title': 'Cold Start Stiffness',
    'calibration.q.coldStart.desc': 'Do you struggle to get moving in cold weather?',
    'calibration.q.coldStart.yes': 'Yes',
    'calibration.q.coldStart.yesDesc': 'Need long warmup, stiff muscles.',
    'calibration.q.coldStart.some': 'Some',
    'calibration.q.coldStart.someDesc': 'A bit slow at first.',
    'calibration.q.coldStart.no': 'No',
    'calibration.q.coldStart.noDesc': 'No issues, ready to go.',
    // Execution Scenarios
    'scenarios.title': 'Execution Strategy',
    'scenarios.desc': 'This global setting applies friction and risk buffers to every segment, stop time, and nutrition target.',
    'scenarios.optimal': 'Optimal',
    'scenarios.optimalDesc': 'Clean execution. Ideal conditions. Baseline pacing.',
    'scenarios.standard': 'Standard',
    'scenarios.standardDesc': 'Realistic friction. Terrain drag + stop buffers. (Recommended)',
    'scenarios.safe': 'Safe',
    'scenarios.safeDesc': 'Conservative. Late-race fatigue + night penalties + extra stops.',
    'scenarios.climateNote': "Climate Interaction: Your profile's heat/cold sensitivity will automatically amplify or reduce these scenario effects based on the weather forecast.",
    // Weather
    'weather.title': 'Weather Forecast',
    'weather.subtitle': 'Forecast from {date} · Based on race start location',
    'weather.loading': 'Loading weather forecast...',
    'weather.unavailable': 'Weather forecast unavailable',
    'weather.wind': 'Wind',
    'weather.precip': 'Precip',
    'weather.humidity': 'Humidity',
    // Weather time blocks
    'weather.block.Morning': 'Morning',
    'weather.block.Afternoon': 'Afternoon',
    'weather.block.Evening': 'Evening',
    'weather.block.Night': 'Night',
    // Weather descriptions (WMO codes)
    'weather.code.0': 'Clear sky',
    'weather.code.1': 'Mainly clear',
    'weather.code.2': 'Partly cloudy',
    'weather.code.3': 'Overcast',
    'weather.code.fog': 'Fog',
    'weather.code.drizzle': 'Light drizzle',
    'weather.code.freezingDrizzle': 'Freezing drizzle',
    'weather.code.lightRain': 'Light rain',
    'weather.code.moderateRain': 'Moderate rain',
    'weather.code.heavyRain': 'Heavy rain',
    'weather.code.freezingRain': 'Freezing rain',
    'weather.code.lightSnow': 'Light snow',
    'weather.code.moderateSnow': 'Moderate snow',
    'weather.code.heavySnow': 'Heavy snow',
    'weather.code.snowGrains': 'Snow grains',
    'weather.code.lightShowers': 'Light showers',
    'weather.code.moderateShowers': 'Moderate showers',
    'weather.code.heavyShowers': 'Heavy showers',
    'weather.code.snowShowers': 'Snow showers',
    'weather.code.thunderstorm': 'Thunderstorm',
    'weather.code.thunderstormHail': 'Thunderstorm w/ hail',
    'weather.code.unknown': 'Unknown',
    // Weather alerts
    'weather.alert.storm': 'Thunderstorm activity expected during the race',
    'weather.alert.heavyRain': 'Heavy precipitation expected (up to {value} mm/h)',
    'weather.alert.snow': 'Snowfall expected during the race',
    'weather.alert.strongWind': 'Strong winds expected (up to {value} km/h)',
    'weather.alert.moderateWind': 'Moderate winds expected (up to {value} km/h)',
    'weather.alert.freezing': 'Freezing temperatures expected (down to {value}°C)',
    'weather.alert.cold': 'Cold conditions expected (down to {value}°C)',
    'weather.alert.extremeHeat': 'Extreme heat expected (up to {value}°C)',
    'weather.alert.hot': 'Hot conditions expected (up to {value}°C)'
  },
  pt: {
    // Navigation
    'nav.route': 'Percurso',
    'nav.pacing': 'Ritmo',
    'nav.nutrition': 'Nutrição',
    'nav.settings': 'Definições',
    // Header
    'header.export': 'Exportar plano',
    'header.import': 'Importar plano',
    'header.newPlan': 'Iniciar um novo plano?',
    'header.help': 'Ajuda e Suporte',
    // Import Screen
    'import.title1': 'Planeia a tua corrida.',
    'import.title2': 'Domina o trilho.',
    'import.subtitle': 'Importa o teu ficheiro GPX e obtém estratégias de ritmo detalhadas, planeamento de postos e insights para o dia da corrida.',
    'import.dropTitle': 'Arrasta o teu ficheiro GPX aqui',
    'import.dropSubtitle': 'ou clica para procurar no teu dispositivo',
    'import.selectGpx': 'Selecionar Ficheiro GPX',
    'import.or': 'ou',
    'import.racePlan': 'Importar Plano de Corrida',
    'import.racePlanDesc': 'Carrega um ficheiro .json previamente exportado com percurso, ritmo e nutrição',
    'import.demo': 'Ou experimenta com um percurso demo →',
    'import.features.checkpoints': 'Postos',
    'import.features.pacing': 'Ritmo',
    'import.features.strategy': 'Estratégia',
    'import.footer': 'Suporta ficheiros GPX do Strava, Garmin, Coros e outras plataformas',
    // Course Tab
    'course.raceName': 'Nome da Corrida',
    'course.raceNamePlaceholder': 'Introduzir nome da corrida',
    'course.startDateTime': 'Data e Hora de Partida',
    'course.title': 'Percurso',
    'course.subtitle': 'Define o percurso, detalhes da corrida e gere os postos de controlo',
    'course.distance': 'Distância',
    'course.elevationPlus': 'Desnível +',
    'course.elevationMinus': 'Desnível -',
    'course.sunrise': 'Nascer do Sol',
    'course.sunset': 'Pôr do Sol',
    'course.elevation': 'Elevação',
    'course.map': 'Mapa',
    'course.route': 'Percurso',
    'course.elevationProfile': 'Perfil de Elevação',
    'course.clickToAdd': 'Clica para adicionar posto',
    'course.tapToAdd': 'Toca no gráfico para adicionar posto',
    'course.checkpoints': 'Postos de Controlo',
    'course.checkpointsDesc': 'Abastecimentos, cutoffs e segmentos',
    'course.add': 'Adicionar',
    // Segments Table
    'segments.name': 'Nome',
    'segments.type': 'Tipo',
    'segments.terrain': 'Terreno',
    'segments.dist': 'Dist',
    'segments.dPlus': 'D+',
    'segments.cutoff': 'Cutoff',
    'segments.noCheckpoints': 'Sem postos de controlo',
    'segments.noCheckpointsDesc': 'Adiciona um split para criar segmentos',
    'segments.checkpointName': 'Nome do posto',
    // Column tooltips — Segments
    'tooltip.segments.name': 'Nome ou identificador do posto de controlo ou abastecimento.',
    'tooltip.segments.type': 'Categoria do posto: Base de Vida, Abastecimento, Ponto de Água ou Outro.',
    'tooltip.segments.terrain': 'Tipo de superfície deste segmento — afeta os cálculos de ritmo.',
    'tooltip.segments.dist': 'Distância percorrida neste segmento (km).',
    'tooltip.segments.dPlus': 'Desnível positivo total neste segmento (metros).',
    'tooltip.segments.cutoff': 'Tempo máximo permitido desde a partida para chegar a este posto.',
    // Column tooltips — Pacing
    'tooltip.pacing.checkpoint': 'Nome do posto de controlo ou abastecimento no final deste segmento.',
    'tooltip.pacing.type': 'Categoria do posto: Base de Vida, Abastecimento, Ponto de Água ou Outro.',
    'tooltip.pacing.terrain': 'Tipo de superfície deste segmento — afeta os cálculos de ritmo.',
    'tooltip.pacing.dist': 'Distância percorrida neste segmento (km).',
    'tooltip.pacing.dPlus': 'Desnível positivo total neste segmento (metros).',
    'tooltip.pacing.effortPace': 'Ajustado ao terreno e elevação. Reflete esforço, não ritmo equivalente em plano — não comparável diretamente entre segmentos.',
    'tooltip.pacing.stop': 'Tempo de paragem planeado neste posto (minutos).',
    'tooltip.pacing.segment': 'Tempo total deste segmento incluindo tempo de paragem.',
    'tooltip.pacing.eta': 'Hora estimada de chegada a este posto de controlo.',
    'tooltip.pacing.buffer': 'Margem de tempo antes do cutoff. Verde = seguro, amarelo = apertado, vermelho = ultrapassado.',
    // Column tooltips — Nutrition
    'tooltip.nutrition.cho': 'Hidratos de carbono: ingestão planeada vs. objetivo para este segmento (gramas).',
    'tooltip.nutrition.cal': 'Calorias: ingestão planeada vs. objetivo para este segmento (kcal).',
    // Column tooltips — Product Library
    'tooltip.products.product': 'Nome do produto de nutrição.',
    'tooltip.products.unit': 'Unidade de dose — ex. gel, barra, colher, garrafa.',
    'tooltip.products.cho': 'Hidratos de carbono por unidade (gramas).',
    'tooltip.products.cal': 'Calorias por unidade (kcal).',
    'tooltip.products.sodium': 'Sódio por unidade (miligramas).',
    // Checkpoint Types
    'checkpoint.None': 'Nenhum',
    'checkpoint.BV': 'Base de Vida',
    'checkpoint.Aid': 'Abastecimento',
    'checkpoint.Water': 'Apenas Água',
    'checkpoint.Other': 'Outros',
    'checkpoint.short.None': '—',
    'checkpoint.short.BV': 'BV',
    'checkpoint.short.Aid': 'Abast.',
    'checkpoint.short.Water': 'Água',
    'checkpoint.short.Other': 'Outros',
    // Terrain Types
    'terrain.Road': 'Estrada',
    'terrain.SmoothTrail': 'Trilho Fácil',
    'terrain.MixedTrail': 'Trilho Misto',
    'terrain.Rocky': 'Rochoso',
    'terrain.Technical': 'Técnico',
    'terrain.MudSand': 'Lama / Areia',
    'terrain.OfftrailSteep': 'Fora trilho / Íngreme',
    // Pacing
    'pacing.title': 'Estratégia de Ritmo',
    'pacing.subtitle': 'Configura tempos de paragem e ajusta o ritmo por segmento',
    'pacing.model': 'Modelo',
    'pacing.pacingModel': 'Modelo de Ritmo:',
    'pacing.customOverridden': 'Personalizado (Alterado)',
    'pacing.custom': 'Personalizado',
    'pacing.executionStrategy': 'Estratégia de Execução:',
    'pacing.totalTime': 'Tempo Total',
    'pacing.finish': 'Chegada',
    'pacing.checkpoint': 'Posto',
    'pacing.pace': 'Ritmo',
    'pacing.paceUnit': 'min/km',
    'pacing.stop': 'Paragem',
    'pacing.stopTime': 'Tempo Paragem',
    'pacing.segment': 'Segmento',
    'pacing.elapsed': 'Acumulado',
    'pacing.accumulated': 'Acumulado',
    'pacing.eta': 'ETA',
    'pacing.effortPace': 'Ritmo Esforço',
    'pacing.buffer': 'Margem',
    'pacing.scenario': 'Cenário de Execução',
    'pacing.segments': '{count} segmentos',
    'pacing.night': 'Noite',
    'pacing.day': 'Dia',
    'pacing.hot': 'Calor',
    'pacing.baseCamp': 'BASE DE VIDA',
    'pacing.long': 'Longo',
    'pacing.steep': 'Íngreme',
    'scenario.optimal': 'Ótimo',
    'scenario.standard': 'Padrão',
    'scenario.safe': 'Seguro',
    // Nutrition
    'nutrition.title': 'Plano de Nutrição',
    'nutrition.subtitle': 'Planeia a tua estratégia de alimentação por segmento',
    'nutrition.choBalance': 'Balanço CHO',
    'nutrition.calBalance': 'Balanço CAL',
    'nutrition.productsUsed': 'Produtos Usados',
    'nutrition.totalUsage': 'Consumo Total de Produtos',
    'nutrition.needed': 'necessário',
    'nutrition.ofLibrary': 'de {count} na biblioteca',
    'nutrition.addProduct': 'Adicionar produto…',
    'nutrition.noProducts': 'Sem produtos adicionados',
    'nutrition.noPacing': 'Sem dados de ritmo',
    'nutrition.noPacingDesc': 'Configura o percurso e ritmo primeiro',
    'nutrition.overallStatus': 'Estado Geral',
    'nutrition.segmentBreakdown': 'Detalhe por Segmento',
    'nutrition.packingList': 'Lista de Material',
    'nutrition.itemsToCarry': '{count} itens para levar',
    'nutrition.plannedProducts': 'Produtos Planeados',
    'nutrition.noProductsYet': 'Sem produtos adicionados',
    'nutrition.tapToPlan': 'Toca para planejar',
    'nutrition.fluidGuidance': 'Objetivo: ~{amount}ml de líquidos. Apenas orientação.',
    'nutrition.recommendedFluid': 'Ingestão recomendada de líquido:',
    'nutrition.searchProduct': 'Pesquisar produto para adicionar...',
    'nutrition.productLibraryDesc': 'Gere os teus produtos de nutrição para planeamento de corrida.',
    'nutrition.stateIncomplete': 'Incompleto',
    'nutrition.stateNeedsAdjustment': 'Necessita ajuste',
    'nutrition.stateOnTarget': 'No objetivo',
    'nutrition.aboveTargetNote': 'A ingestão planeada está acima do objetivo recomendado. Isto é aceitável para segmentos mais curtos ou preferência pessoal — apenas orientação.',
    'nutrition.targets': 'Objetivos Nutricionais',
    'nutrition.targetsSubtitle': 'Ingestão horária que guia o teu plano de nutrição',
    'nutrition.targetsInfo': 'Estes valores são derivados do teu perfil de atleta (nível de esforço e taxa de suor). Podes ajustá-los aqui — as alterações aplicam-se diretamente aos objetivos do plano de nutrição.',
    'nutrition.carbs': 'Hidratos',
    'nutrition.calories': 'Calorias',
    'nutrition.fluids': 'Líquidos',
    'nutrition.sodium': 'Sódio',
    'nutrition.carbsDesc': 'Objetivo de ingestão de hidratos de carbono por hora. Esforços mais intensos requerem mais hidratos. A maioria dos atletas tolera 60–90g/h; intestino treinado pode suportar até 120g/h.',
    'nutrition.caloriesDesc': 'Objetivo de ingestão calórica por hora. Derivado dos hidratos (~4 kcal por grama). Ajusta se usas combustíveis à base de gordura ou fontes mistas.',
    'nutrition.fluidsDesc': 'Objetivo de ingestão de líquidos por hora. Quem sua mais e em condições quentes precisa de mais. Tenta repor 60–80% das perdas de suor. Beber em excesso pode ser tão perigoso como beber pouco.',
    'nutrition.sodiumDesc': 'Objetivo de ingestão de sódio por hora. Crucial para prevenir hiponatremia e cãibras. Quem sua muito e em condições quentes precisa de mais. Intervalo típico: 300–800 mg/h dependendo da taxa de suor.',
    // Settings (PT)
    'settings.title': 'Definições',
    'settings.subtitle': 'Gere os teus produtos, objetivos de nutrição e modelos de ritmo',
    'settings.productLibrary': 'Biblioteca de Produtos',
    'settings.nutrition': 'Nutrição',
    'settings.pacingModels': 'Modelos de Ritmo',
    'settings.pacingModel': 'Modelo de Ritmo',
    'settings.pacingModelNote': 'Todos os modelos são automaticamente calibrados usando o teu Perfil de Atleta global. Sem necessidade de ajuste manual.',
    'settings.raceDefaults': 'Predefinições',
    'settings.scenarios': 'Cenários de Execução',
    'settings.scenariosDesc': 'Ajusta o conservadorismo do plano. Padrão adiciona fricção por terreno e fadiga; Seguro adiciona margens extra para gestão de risco.',
    'settings.safeIntensity': 'Intensidade Modo Seguro',
    'settings.safeIntensityDesc': 'Quão agressivamente o cenário "Seguro" deve aplicar penalizações?',
    'settings.safeIntensityNote': 'Ajusta a severidade das penalizações de fadiga final no modo Seguro.',
    'intensity.low': 'Baixa',
    'intensity.default': 'Padrão',
    'intensity.high': 'Alta',
    'settings.terrainFactors': 'Fatores de Terreno',
    'settings.terrainFactorsDesc': 'Ajusta o multiplicador de ritmo, penalização de subida e ajuste de descida por tipo de terreno',
    'settings.terrainFactor': 'Fator Ritmo',
    'settings.uphillSec': 'Subida (s/100m)',
    'settings.downhillSec': 'Descida (s/100m)',
    'settings.resetDefaults': 'Repor Predefinições',
    'settings.search': 'Pesquisar',
    'settings.searchProducts': 'Pesquisar produtos…',
    'settings.export': 'Exportar',
    'settings.exportProducts': 'Exportar Produtos',
    'settings.import': 'Importar',
    'settings.importProducts': 'Importar Produtos',
    'settings.addProduct': 'Adicionar Produto',
    'settings.productName': 'Nome',
    'settings.unit': 'Unidade',
    'settings.carbs': 'Hidratos (g)',
    'settings.cals': 'Calorias',
    'settings.sodium': 'Sódio (mg)',
    'settings.save': 'Guardar',
    'settings.cancel': 'Cancelar',
    'settings.product': 'Produto',
    'settings.noProducts': 'Sem produtos',
    'settings.noMatch': 'Nenhum produto encontrado',
    'settings.removeProduct': 'Remover "{name}" da biblioteca?',
    'settings.hourlyTargets': 'Objetivos por Hora',
    'settings.hourlyTargetsDesc': 'Objetivos padrão aplicados a todos os segmentos',
    'settings.choTarget': 'Objetivo CHO (g/h)',
    'settings.calTarget': 'Objetivo CAL (kcal/h)',
    'settings.alertThresholds': 'Limites de Alerta',
    'settings.alertThresholdsDesc': 'Destacar défices a vermelho quando excedem estes valores',
    'settings.choDeficitAlert': 'Alerta Défice CHO (g)',
    'settings.calDeficitAlert': 'Alerta Défice CAL (kcal)',
    'settings.active': 'Ativo',
    'settings.activate': 'Ativar',
    'settings.language': 'Idioma',
    'settings.athleteProfile': 'Perfil do Atleta',
    'settings.executionStrategy': 'Estratégia de Execução',
    'settings.checkpointStopDefaults': 'Paragens por Defeito nos Postos (min)',
    // Product table headers
    'products.product': 'Produto',
    'products.unit': 'Unidade',
    'products.cho': 'CHO',
    'products.cal': 'CAL',
    'products.sodium': 'Na+',
    'products.removeTitle': 'Remover Produto',
    'products.removeConfirm': 'Eliminar',
    'products.delete': 'Eliminar {name}?',
    // Checkpoint stop defaults
    'settings.checkpointDefaults': 'Paragens por Defeito',
    'settings.checkpointDefaultsDesc': 'Tempo de paragem aplicado automaticamente ao selecionar um tipo de posto',
    'settings.defaultStop.Water': 'Apenas Água (min)',
    'settings.defaultStop.Aid': 'Abastecimento (min)',
    'settings.defaultStop.BV': 'Base de Vida (min)',
    'settings.defaultStop.Other': 'Outros (min)',
    // Add Split Dialog
    'split.title': 'Adicionar Posto',
    'split.totalCourse': 'Percurso total:',
    'split.distance': 'Distância (km)',
    'split.distancePlaceholder': 'ex. 12.5',
    'split.name': 'Nome (opcional)',
    'split.namePlaceholder': 'ex. Abastecimento 1',
    'split.distanceError': 'Introduz uma distância entre 0 e {max} km',
    'split.add': 'Adicionar Posto',
    // Common
    'common.cancel': 'Cancelar',
    'common.save': 'Guardar',
    'common.back': 'Voltar',
    'common.next': 'Seguinte',
    'common.min': 'min',
    'common.total': 'Total',
    'common.fromLibrary': 'Da tua biblioteca',
    'common.confirm': 'Confirmar',
    // Confirm dialogs
    'confirm.newPlan.title': 'Iniciar um novo plano?',
    'confirm.newPlan.message': 'Isto irá apagar todos os dados atuais, incluindo percurso, ritmo e planos de nutrição. Esta ação não pode ser desfeita.',
    'confirm.newPlan.confirm': 'Sim, iniciar novo',
    'confirm.removeProduct.title': 'Remover produto?',
    'confirm.removeProduct.message': 'Remover "{name}" da tua biblioteca? Esta ação não pode ser desfeita.',
    'confirm.removeProduct.confirm': 'Remover',
    // Athlete Profile
    'athlete.title': 'Perfil do Atleta',
    'athlete.subtitle': 'A tua calibração personalizada que guia todos os cálculos.',
    'athlete.recalibrate': 'Recalibrar',
    'athlete.flatEffort': 'Esforço Plano',
    'athlete.uphillStrategy': 'Estratégia Subida',
    'athlete.heatTolerance': 'Tolerância Calor',
    'athlete.sweatRate': 'Taxa de Suor',
    'athlete.fast': 'Rápido',
    'athlete.insightsTitle': 'O que isto significa para a tua corrida:',
    'athlete.insightHeat': 'Sensibilidade ao Calor: O plano inclui hidratação agressiva e aumento de sódio em tempo quente.',
    'athlete.insightSweat': 'Suor Intenso: Os objetivos de hidratação base estão elevados (+30-40%).',
    'athlete.insightFatigue': 'Fadiga Precoce: O modelo de ritmo aplica decaimento conservador após 3 horas.',
    'athlete.insightRisk': 'Estratégia Conservadora: O plano favorece margens de segurança e arranques mais lentos.',
    // Calibration Wizard
    'calibration.title': 'Calibração do Atleta',
    'calibration.welcome': 'Bem-vindo',
    'calibration.review': 'Revisão',
    'calibration.questionOf': 'Pergunta {current} de {total}',
    'calibration.introTitle': 'Vamos calibrar o teu perfil',
    'calibration.introDesc': 'Para gerar um plano de corrida preciso, precisamos de compreender o teu estilo de corrida, resistência à fadiga e sensibilidade climática. Demora cerca de 1 minuto.',
    'calibration.benefit1': 'Determina o teu ritmo sustentável e necessidades de alimentação',
    'calibration.benefit2': 'Define a estratégia de subida/descida automaticamente',
    'calibration.benefit3': 'Ajusta para calor, frio e fadiga de final de corrida',
    'calibration.start': 'Iniciar Calibração',
    'calibration.reviewTitle': 'Perfil Calibrado!',
    'calibration.reviewDesc': 'O teu perfil de atleta está pronto. Derivámos o teu ritmo base, objetivos de nutrição e ajustes climáticos.',
    'calibration.saveProfile': 'Guardar Perfil e Gerar Plano',
    'calibration.flatPace': 'Ritmo Plano',
    'calibration.uphill': 'Subida',
    'calibration.downhill': 'Descida',
    'calibration.fatigueOnset': 'Início Fadiga',
    'calibration.riskProfile': 'Perfil de Risco',
    'calibration.coldStart': 'Arranque a Frio',
    // Calibration Questions
    'calibration.q.flatEffort.title': 'Esforço Sustentável em Plano',
    'calibration.q.flatEffort.desc': 'Num trilho plano e corrível após várias horas, qual é o teu ritmo sustentável?',
    'calibration.q.flatEffort.sub530': 'Rápido (< 5:30 min/km)',
    'calibration.q.flatEffort.sub530Desc': 'Alta intensidade, corredor eficiente.',
    'calibration.q.flatEffort.steady': 'Firme (5:30 - 6:00 min/km)',
    'calibration.q.flatEffort.steadyDesc': 'Esforço aeróbico forte e controlado.',
    'calibration.q.flatEffort.comfortable': 'Confortável (6:00 - 6:30 min/km)',
    'calibration.q.flatEffort.comfortableDesc': 'Ritmo de conversa, resistência.',
    'calibration.q.flatEffort.easy': 'Fácil (> 6:30 min/km)',
    'calibration.q.flatEffort.easyDesc': 'Ritmo conservador, foco em terminar.',
    'calibration.q.uphillStrategy.title': 'Estratégia de Subida',
    'calibration.q.uphillStrategy.desc': 'Como lidas com subidas longas?',
    'calibration.q.uphillStrategy.hikeEarly': 'Caminhar Cedo',
    'calibration.q.uphillStrategy.hikeEarlyDesc': 'Mudar para caminhada rapidamente para poupar pernas.',
    'calibration.q.uphillStrategy.mixed': 'Misto',
    'calibration.q.uphillStrategy.mixedDesc': 'Correr inclinações suaves, caminhar nas íngremes.',
    'calibration.q.uphillStrategy.runMost': 'Correr a Maioria',
    'calibration.q.uphillStrategy.runMostDesc': 'Subida agressiva, correr o máximo possível.',
    'calibration.q.downhillHandling.title': 'Gestão de Descida',
    'calibration.q.downhillHandling.desc': 'Como lidas com descidas técnicas?',
    'calibration.q.downhillHandling.cautious': 'Cauteloso',
    'calibration.q.downhillHandling.cautiousDesc': 'Abrando significativamente para estar seguro.',
    'calibration.q.downhillHandling.controlled': 'Controlado',
    'calibration.q.downhillHandling.controlledDesc': 'Ritmo estável, sem grandes riscos.',
    'calibration.q.downhillHandling.aggressive': 'Agressivo',
    'calibration.q.downhillHandling.aggressiveDesc': 'Ataco as descidas e recupero tempo.',
    'calibration.q.fatigue.title': 'Início da Fadiga',
    'calibration.q.fatigue.desc': 'Quando começas tipicamente a sentir fadiga significativa?',
    'calibration.q.fatigue.early': '< 3 Horas',
    'calibration.q.fatigue.earlyDesc': 'Resistência curta.',
    'calibration.q.fatigue.mid': '3 - 5 Horas',
    'calibration.q.fatigue.midDesc': 'Resistência média.',
    'calibration.q.fatigue.late': '5 - 7 Horas',
    'calibration.q.fatigue.lateDesc': 'Resistência longa.',
    'calibration.q.fatigue.ultra': '8+ Horas',
    'calibration.q.fatigue.ultraDesc': 'Ultra resistência.',
    'calibration.q.riskTolerance.title': 'Tolerância ao Risco',
    'calibration.q.riskTolerance.desc': 'Qual é o teu objetivo principal para esta corrida?',
    'calibration.q.riskTolerance.finish': 'Terminar Primeiro',
    'calibration.q.riskTolerance.finishDesc': 'Priorizar terminar em segurança. Evitar rebentar.',
    'calibration.q.riskTolerance.balanced': 'Equilibrado',
    'calibration.q.riskTolerance.balancedDesc': 'Equilibrar desempenho com segurança.',
    'calibration.q.riskTolerance.performance': 'Desempenho',
    'calibration.q.riskTolerance.performanceDesc': 'Forçar limites para um recorde. Aceitar risco maior.',
    'calibration.q.heatTolerance.title': 'Tolerância ao Calor',
    'calibration.q.heatTolerance.desc': 'Como o calor afeta o teu desempenho?',
    'calibration.q.heatTolerance.sensitive': 'Sensível',
    'calibration.q.heatTolerance.sensitiveDesc': 'O calor abranda-me significativamente cedo.',
    'calibration.q.heatTolerance.moderate': 'Moderado',
    'calibration.q.heatTolerance.moderateDesc': 'Noto, mas lido bem.',
    'calibration.q.heatTolerance.tolerant': 'Tolerante',
    'calibration.q.heatTolerance.tolerantDesc': 'Tenho bom desempenho em condições quentes.',
    'calibration.q.sweatRate.title': 'Taxa de Suor Percebida',
    'calibration.q.sweatRate.desc': 'Quanto suas comparado com outros?',
    'calibration.q.sweatRate.heavy': 'Muito',
    'calibration.q.sweatRate.heavyDesc': 'Encharco a roupa rapidamente. Marcas de sal.',
    'calibration.q.sweatRate.normal': 'Normal',
    'calibration.q.sweatRate.normalDesc': 'Suor médio.',
    'calibration.q.sweatRate.low': 'Pouco',
    'calibration.q.sweatRate.lowDesc': 'Fico relativamente seco.',
    'calibration.q.coldStart.title': 'Rigidez no Arranque a Frio',
    'calibration.q.coldStart.desc': 'Tens dificuldade em arrancar com tempo frio?',
    'calibration.q.coldStart.yes': 'Sim',
    'calibration.q.coldStart.yesDesc': 'Preciso de aquecimento longo, músculos rígidos.',
    'calibration.q.coldStart.some': 'Alguma',
    'calibration.q.coldStart.someDesc': 'Um pouco lento no início.',
    'calibration.q.coldStart.no': 'Não',
    'calibration.q.coldStart.noDesc': 'Sem problemas, pronto para ir.',
    // Execution Scenarios
    'scenarios.title': 'Estratégia de Execução',
    'scenarios.desc': 'Esta definição global aplica fricção e margens de risco a cada segmento, tempo de paragem e objetivo de nutrição.',
    'scenarios.optimal': 'Ótimo',
    'scenarios.optimalDesc': 'Execução limpa. Condições ideais. Ritmo base.',
    'scenarios.standard': 'Padrão',
    'scenarios.standardDesc': 'Fricção realista. Arrasto de terreno + margens de paragem. (Recomendado)',
    'scenarios.safe': 'Seguro',
    'scenarios.safeDesc': 'Conservador. Fadiga final + penalizações noturnas + paragens extra.',
    'scenarios.climateNote': 'Interação Climática: A sensibilidade ao calor/frio do teu perfil vai automaticamente amplificar ou reduzir estes efeitos de cenário com base na previsão meteorológica.',
    // Weather
    'weather.title': 'Previsão Meteorológica',
    'weather.subtitle': 'Previsão de {date} · Baseado na localização de partida',
    'weather.loading': 'A carregar previsão meteorológica...',
    'weather.unavailable': 'Previsão meteorológica indisponível',
    'weather.wind': 'Vento',
    'weather.precip': 'Precip',
    'weather.humidity': 'Humidade',
    // Weather time blocks
    'weather.block.Morning': 'Manhã',
    'weather.block.Afternoon': 'Tarde',
    'weather.block.Evening': 'Noite',
    'weather.block.Night': 'Madrugada',
    // Weather descriptions (WMO codes)
    'weather.code.0': 'Céu limpo',
    'weather.code.1': 'Predominantemente limpo',
    'weather.code.2': 'Parcialmente nublado',
    'weather.code.3': 'Encoberto',
    'weather.code.fog': 'Nevoeiro',
    'weather.code.drizzle': 'Chuvisco ligeiro',
    'weather.code.freezingDrizzle': 'Chuvisco gelado',
    'weather.code.lightRain': 'Chuva fraca',
    'weather.code.moderateRain': 'Chuva moderada',
    'weather.code.heavyRain': 'Chuva forte',
    'weather.code.freezingRain': 'Chuva gelada',
    'weather.code.lightSnow': 'Neve fraca',
    'weather.code.moderateSnow': 'Neve moderada',
    'weather.code.heavySnow': 'Neve forte',
    'weather.code.snowGrains': 'Grãos de neve',
    'weather.code.lightShowers': 'Aguaceiros fracos',
    'weather.code.moderateShowers': 'Aguaceiros moderados',
    'weather.code.heavyShowers': 'Aguaceiros fortes',
    'weather.code.snowShowers': 'Aguaceiros de neve',
    'weather.code.thunderstorm': 'Trovoada',
    'weather.code.thunderstormHail': 'Trovoada com granizo',
    'weather.code.unknown': 'Desconhecido',
    // Weather alerts
    'weather.alert.storm': 'Atividade de trovoada esperada durante a corrida',
    'weather.alert.heavyRain': 'Precipitação forte esperada (até {value} mm/h)',
    'weather.alert.snow': 'Queda de neve esperada durante a corrida',
    'weather.alert.strongWind': 'Ventos fortes esperados (até {value} km/h)',
    'weather.alert.moderateWind': 'Ventos moderados esperados (até {value} km/h)',
    'weather.alert.freezing': 'Temperaturas negativas esperadas (até {value}°C)',
    'weather.alert.cold': 'Condições frias esperadas (até {value}°C)',
    'weather.alert.extremeHeat': 'Calor extremo esperado (até {value}°C)',
    'weather.alert.hot': 'Condições quentes esperadas (até {value}°C)'
  }
};
interface I18nContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}
const I18nContext = createContext<I18nContextType>({
  locale: 'en',
  setLocale: () => {},
  t: key => key
});
export function I18nProvider({
  children
}: {
  children: ReactNode;
}) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const saved = localStorage.getItem('trail-chronos-locale');
    return saved === 'pt' || saved === 'en' ? saved : 'en';
  });
  const setLocale = useCallback((newLocale: Locale) => {
    setLocaleState(newLocale);
    localStorage.setItem('trail-chronos-locale', newLocale);
  }, []);
  const t = useCallback((key: string, params?: Record<string, string | number>) => {
    let value = translations[locale][key] || translations['en'][key] || key;
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        value = value.replace(`{${k}}`, String(v));
      });
    }
    return value;
  }, [locale]);
  return <I18nContext.Provider value={{
    locale,
    setLocale,
    t
  }} data-id="element-1276">
      {children}
    </I18nContext.Provider>;
}
export function useTranslation() {
  return useContext(I18nContext);
}
// SVG flag components for cross-platform support (emoji flags don't work on Windows)
export function FlagGB({
  className
}: {
  className?: string;
}) {
  return <svg className={className} viewBox="0 0 60 30" xmlns="http://www.w3.org/2000/svg" data-id="element-1277">
      <clipPath id="s" data-id="element-1278">
        <path d="M0,0 v30 h60 v-30 z" data-id="element-1279" />
      </clipPath>
      <clipPath id="t" data-id="element-1280">
        <path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" data-id="element-1281" />
      </clipPath>
      <g clipPath="url(#s)" data-id="element-1282">
        <path d="M0,0 v30 h60 v-30 z" fill="#012169" data-id="element-1283" />
        <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" data-id="element-1284" />
        <path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#t)" stroke="#C8102E" strokeWidth="4" data-id="element-1285" />
        <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10" data-id="element-1286" />
        <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" data-id="element-1287" />
      </g>
    </svg>;
}
export function FlagPT({
  className
}: {
  className?: string;
}) {
  return <svg className={className} viewBox="0 0 600 400" xmlns="http://www.w3.org/2000/svg" data-id="element-1288">
      <rect width="600" height="400" fill="#FF0000" data-id="element-1289" />
      <rect width="240" height="400" fill="#006600" data-id="element-1290" />
      <circle cx="240" cy="200" r="64" fill="#FFCC00" data-id="element-1291" />
      <circle cx="240" cy="200" r="52" fill="#FF0000" data-id="element-1292" />
      <circle cx="240" cy="200" r="44" fill="#fff" data-id="element-1293" />
      <path d="M218,172 v56 h44 v-56 z" fill="#003399" opacity="0.8" data-id="element-1294" />
    </svg>;
}
export const LOCALE_OPTIONS: {
  value: Locale;
  label: string;
  FlagComponent: React.FC<{
    className?: string;
  }>;
}[] = [{
  value: 'en',
  label: 'English',
  FlagComponent: FlagGB
}, {
  value: 'pt',
  label: 'Português',
  FlagComponent: FlagPT
}];